import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker, { renderSchoolLogo } from '../components/SchoolPicker';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const SEARCH_INPUT = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: '"Baikal Book"',
};

const ROW = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '12px 0',
  borderBottom: '1px solid #f3f4f6',
};

// `minWidth: 0` alone only lets the flex item shrink — the text inside still renders
// at its full width and spills into the next column, because an email address has no
// spaces to wrap on. `overflowWrap: 'anywhere'` gives the browser permission to break
// mid-string. Wrapping rather than truncating, since on this page the email is the
// identifier being verified and a clipped one can't be told from a similar address.
const COL_BASE = { minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' };

const COL_NAME = { ...COL_BASE, flex: '1 1 220px', fontFamily: '"Baikal Book"', fontSize: '15px' };
const COL_EMAIL = { ...COL_BASE, flex: '1 1 240px', fontFamily: '"Baikal Book"', fontSize: '14px', color: '#4b5563' };
// Just the crest, so it needs far less room than the name did.
const COL_SCHOOL = { ...COL_BASE, flex: '0 0 32px', display: 'flex', alignItems: 'center' };
const COL_ROLE = { ...COL_BASE, flex: '0 0 90px', fontFamily: '"Baikal Book"', fontSize: '13px', color: '#6b7280' };

// The row action is the other squeeze victim: as a flex item it defaults to
// shrinkable, so a long email would compress it until its own label wrapped.
const ROW_ACTION = { flexShrink: 0, alignSelf: 'center' };

// The crest carries no text, so it needs a title for anyone who can't place it.
function SchoolCell({ school }) {
  if (!school) {
    return <div style={{ ...COL_SCHOOL, color: '#9ca3af', fontSize: '14px' }}>—</div>;
  }
  return (
    <div style={COL_SCHOOL} title={school.name || ''}>
      {renderSchoolLogo(school, 28)}
    </div>
  );
}

function displayName(user) {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email;
}

function roleLabel(user) {
  if (user.is_superuser) return 'Superuser';
  if (user.is_staff) return 'Staff';
  return 'User';
}

const SuperuserRewardExclusions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hiddenUsers, setHiddenUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [choseAll, setChoseAll] = useState(false);

  const schoolId = selectedSchool?.id || '';
  const allSchools = choseAll && !selectedSchool;

  const loadHidden = useCallback(async () => {
    if (!schoolId && !allSchools) {
      setHiddenUsers([]);
      return;
    }
    const params = schoolId ? { school_id: schoolId } : {};
    const data = await apiService.getRewardExclusionsSuperuser(params);
    setHiddenUsers(Array.isArray(data.users) ? data.users : []);
  }, [schoolId, allSchools]);

  useEffect(() => {
    const boot = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        try {
          const data = await apiService.getSchools();
          const list = Array.isArray(data) ? data : data?.results || [];
          setSchools([...list].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        } catch (schoolErr) {
          // A missing school list only costs the filter, so don't fail the page.
          console.error('Failed to load schools:', schoolErr);
          setSchools([]);
        }
      } catch (err) {
        console.error('Failed to load reward exclusions:', err);
        setError('Could not load hidden users.');
      } finally {
        setLoading(false);
      }
    };
    boot();
    // Deliberately once on mount: the school-change reload is its own effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Re-scope the hidden list when the school filter changes (skipped on first
  // render, which boot() already covers).
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    loadHidden().catch((err) => {
      console.error('Failed to reload hidden users:', err);
      setError('Could not load hidden users.');
    });
  }, [loadHidden]);

  useEffect(() => {
    const query = searchTerm.trim();
    if (!query || (!schoolId && !allSchools)) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await apiService.getAllUsersSuperuser({
          search: query,
          page_size: 8,
          ...(schoolId ? { school_id: schoolId } : {}),
        });
        const hiddenIds = new Set(hiddenUsers.map((u) => String(u.id)));
        setSearchResults((data.users || []).filter((u) => !hiddenIds.has(String(u.id))));
      } catch (err) {
        console.error('Failed to search users:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, hiddenUsers, schoolId, allSchools]);

  const hideUser = async (user) => {
    setError('');
    setNotice('');
    setBusyId(user.id);
    try {
      await apiService.addRewardExclusionSuperuser(user.id);
      setSearchTerm('');
      setSearchResults([]);
      await loadHidden();
      setNotice(`${displayName(user)} is hidden from leaderboards and raffle wins.`);
    } catch (err) {
      console.error('Failed to hide user:', err);
      setError(err?.response?.data?.error || 'Could not hide this user.');
    } finally {
      setBusyId(null);
    }
  };

  const unhideUser = async (user) => {
    setError('');
    setNotice('');
    setBusyId(user.id);
    try {
      await apiService.removeRewardExclusionSuperuser(user.id);
      await loadHidden();
      setNotice(`${displayName(user)} can appear on leaderboards and win raffles again.`);
    } catch (err) {
      console.error('Failed to unhide user:', err);
      setError(err?.response?.data?.error || 'Could not unhide this user.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Hidden from Rewards</h1>
          <p className="staff-page-subheader">
            Hidden users do not appear on weekly or season leaderboards and cannot win raffles.
            Any user can be hidden.
          </p>

          {schools.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 8,
                  fontFamily: '"Baikal Book"',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                School
              </label>
              <SchoolPicker
                schools={schools}
                value={selectedSchool}
                onChange={(school) => {
                  setSelectedSchool(school);
                  setChoseAll(!school);
                }}
                allOptionLabel="All schools"
                allSelected={choseAll}
              />
            </div>
          )}

          {!schoolId && !allSchools ? (
            schools.length > 0 ? (
              <p className="staff-page-content" style={{ marginTop: '32px', opacity: 0.7 }}>
                Please select a school
              </p>
            ) : null
          ) : (
          <>
          <div className="add-reward-section" style={{ marginTop: '32px' }}>
            <h2 className="staff-section-header" style={{ marginBottom: '8px' }}>
              Hide a user
            </h2>
            <input
              type="text"
              placeholder="Search by name or email . . ."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...SEARCH_INPUT, marginTop: '12px' }}
            />
            {searching && (
              <p className="staff-page-content" style={{ marginTop: '10px' }}>Searching...</p>
            )}
            {!searching && searchTerm.trim() && searchResults.length === 0 && (
              <p className="staff-page-content" style={{ marginTop: '10px' }}>No matching users.</p>
            )}
            {searchResults.map((user) => (
              <div key={user.id} style={ROW}>
                <div style={COL_NAME}>{displayName(user)}</div>
                <div style={COL_EMAIL}>{user.email}</div>
                <SchoolCell school={user.active_school} />
                <div style={COL_ROLE}>{roleLabel(user)}</div>
                <button
                  type="button"
                  className="staff-page-button"
                  style={ROW_ACTION}
                  onClick={() => hideUser(user)}
                  disabled={busyId === user.id}
                >
                  <span className="staff-page-button-text">
                    {busyId === user.id ? 'Hiding...' : 'Hide'}
                  </span>
                </button>
              </div>
            ))}
          </div>

          <h2 className="staff-section-header" style={{ marginTop: '40px', marginBottom: '8px' }}>
            Hidden users
          </h2>
          <p className="staff-page-content" style={{ margin: '0 0 16px' }}>
            {hiddenUsers.length === 1 ? '1 user hidden' : `${hiddenUsers.length} users hidden`}
          </p>
          {hiddenUsers.length === 0 && (
            <p className="staff-page-content">Nobody is hidden yet.</p>
          )}
          {hiddenUsers.map((user) => (
            <div key={user.id} style={ROW}>
              <div style={COL_NAME}>{displayName(user)}</div>
              <div style={COL_EMAIL}>{user.email}</div>
              <SchoolCell school={user.active_school} />
              <div style={COL_ROLE}>{roleLabel(user)}</div>
              <button
                type="button"
                className="staff-page-button"
                style={ROW_ACTION}
                onClick={() => unhideUser(user)}
                disabled={busyId === user.id}
              >
                <span className="staff-page-button-text">
                  {busyId === user.id ? 'Removing...' : 'Unhide'}
                </span>
              </button>
            </div>
          ))}
          </>
          )}

          {error && (
            <div style={{
              marginTop: '16px',
              padding: '10px 14px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontFamily: '"Baikal Book"',
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}
          {notice && (
            <div style={{
              marginTop: '16px',
              padding: '10px 14px',
              backgroundColor: '#d1fae5',
              border: '1px solid #a7f3d0',
              borderRadius: '8px',
              color: '#065f46',
              fontFamily: '"Baikal Book"',
              fontSize: '13px',
            }}>
              {notice}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserRewardExclusions;
